import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Key, DoorOpen, ClipboardText, Warning, ArrowSquareOut, PawPrint, Copy, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import TruckStockChip from "@/components/TruckStockChip";
import WOVoiceNoteButton from "@/components/WOVoiceNoteButton";
import PhotoOverlayGuide from "@/components/PhotoOverlayGuide";

/**
 * Show-Up Card — the crew's on-the-truck one-screen view of a WO.
 * Renders address + navigate, door codes, tenant tap-to-call, entry permission,
 * "N other WOs at this address" bundler chip, and Callback Radar warnings.
 * Zero scrolling required for the essentials.
 */
export default function ShowUpCard({ job, onReload }) {
  const [texting, setTexting] = useState(false);
  const fm = job._field_mode || {};
  const callbacks = job._callback || [];
  const sameProp = job._same_property_open_count || 0;
  const address = job.property_address || "";
  const unit = job.unit || "";
  const mapsUrl = address ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address + (unit ? " Apt " + unit : ""))}` : null;

  const copy = (label, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  const imHere = async () => {
    setTexting(true);
    try {
      await api.post(`/jobs/${job.id}/en-route`, { arrived: true });
      toast.success("Tenant + admin notified");
      onReload?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't send arrival"));
    } finally { setTexting(false); }
  };

  const entryChip = (() => {
    if (job.entry_permission === "open") return <span className="px-2 py-1 bg-emerald-600 text-white font-black uppercase text-[10px] tracking-widest">✓ ENTER ANYTIME</span>;
    if (job.entry_permission === "no_entry") return <span className="px-2 py-1 bg-red-600 text-white font-black uppercase text-[10px] tracking-widest">✕ DO NOT ENTER</span>;
    return <span className="px-2 py-1 bg-amber-500 text-white font-black uppercase text-[10px] tracking-widest">! CONFIRM ENTRY</span>;
  })();

  return (
    <div data-testid="show-up-card"
      className="border-4 border-[#0f172a] bg-white shadow-[6px_6px_0_#0f172a] mb-4">
      {/* Address + Nav */}
      <div className="p-4 bg-slate-900 text-white">
        <div className="flex items-start gap-3">
          <MapPin weight="fill" size={22} className="text-cyan-300 flex-shrink-0 mt-0.5"/>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-xl leading-tight break-words">{address || "— No address —"}</div>
            {unit && <div className="mt-1 inline-block px-2 py-0.5 bg-fuchsia-500 font-black uppercase text-xs tracking-widest">Apt {unit}</div>}
          </div>
          {mapsUrl && (
            <a href={mapsUrl} target="_blank" rel="noreferrer"
              data-testid="show-up-navigate"
              className="flex-shrink-0 h-11 px-3 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 border-2 border-cyan-300">
              <ArrowSquareOut weight="bold" size={14}/> Navigate
            </a>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {entryChip}
          {sameProp > 0 && (
            <a href="#nearby-jobs" data-testid="show-up-bundler"
              className="px-2 py-1 bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              🎯 {sameProp} MORE WO{sameProp === 1 ? "" : "S"} HERE — BUNDLE?
            </a>
          )}
        </div>
      </div>

      {/* Callback Radar warning */}
      {callbacks.length > 0 && (
        <div className="p-3 bg-red-50 border-b-4 border-red-600" data-testid="callback-radar">
          <div className="flex items-start gap-2">
            <Warning weight="fill" size={18} className="text-red-600 flex-shrink-0 mt-0.5"/>
            <div className="flex-1">
              <div className="font-black uppercase text-[10px] tracking-widest text-red-800">
                🚨 CALLBACK — Same unit was closed {callbacks.length} time{callbacks.length === 1 ? "" : "s"} in last 30 days
              </div>
              <ul className="mt-1 space-y-0.5">
                {callbacks.map(cb => (
                  <li key={cb.id} className="text-xs text-red-900">
                    · <span className="font-black">WO#{cb.work_order_number || cb.id.slice(-6)}</span> {cb.title}
                    <span className="text-slate-500"> · {(cb.completed_at || "").slice(0, 10)}{cb.completed_by_name ? ` · by ${cb.completed_by_name}` : ""}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-1 text-[10px] text-red-700 italic">Bring the same parts + double-check root cause this time.</div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant will NOT be home — lockbox authorized. Sits high in the
          card so the tech doesn't waste a knock or ring the bell. */}
      {job.tenant_not_home && (
        <div className="p-3 bg-red-600 text-white border-b-4 border-red-800" data-testid="tenant-not-home-banner">
          <div className="flex items-start gap-2">
            <Key weight="fill" size={20} className="text-yellow-300 flex-shrink-0 mt-0.5"/>
            <div className="flex-1 min-w-0">
              <div className="font-black uppercase text-xs tracking-widest">
                🔐 LOCKBOX ONLY · TENANT NOT HOME
              </div>
              <div className="text-[11px] mt-0.5 opacity-90">
                Tenant e-signed authorization to enter with the lockbox / entry code — don't wait, don't knock.
                {job.tenant_not_home_signed_name && (
                  <> Authorized by <b>{job.tenant_not_home_signed_name}</b>.</>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Access codes */}
      {(fm.building_door_code || fm.unit_door_code || fm.unit_key_code || fm.laundry_room_key_code || fm.keys_location_note || job.entry_code || job.lockbox_code) && (
        <div className="p-3 bg-cyan-50 border-b-2 border-cyan-500" data-testid="access-codes-card">
          <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800 mb-2">Access Codes</div>
          <div className="grid grid-cols-2 gap-2">
            {fm.building_door_code && <CodeChip icon={<DoorOpen weight="fill" size={14}/>} label="Building" value={fm.building_door_code} onCopy={copy}/>}
            {fm.unit_door_code &&     <CodeChip icon={<DoorOpen weight="fill" size={14}/>} label="Unit Door" value={fm.unit_door_code} onCopy={copy}/>}
            {fm.unit_key_code &&      <CodeChip icon={<Key weight="fill" size={14}/>}      label="Unit Key" value={fm.unit_key_code} onCopy={copy}/>}
            {fm.laundry_room_key_code && <CodeChip icon={<Key weight="fill" size={14}/>}   label="Laundry Key" value={fm.laundry_room_key_code} onCopy={copy}/>}
            {job.entry_code &&        <CodeChip icon={<DoorOpen weight="fill" size={14}/>} label="WO Entry" value={job.entry_code} onCopy={copy}/>}
            {job.lockbox_code &&      <CodeChip icon={<Key weight="fill" size={14}/>}      label="Lockbox" value={job.lockbox_code} onCopy={copy}/>}
          </div>
          {fm.keys_location_note && (
            <div className="mt-2 text-[11px] text-slate-700 italic">📍 {fm.keys_location_note}</div>
          )}
        </div>
      )}

      {/* Tenant + I'M HERE */}
      <div className="p-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        {job.customer_name && (
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Tenant</div>
            <div className="font-black text-sm truncate">{job.customer_name}</div>
            {job.customer_phone && (
              <a href={`tel:${job.customer_phone}`} data-testid="show-up-tenant-call"
                className="inline-flex items-center gap-1 mt-0.5 text-cyan-700 hover:text-cyan-900 text-xs font-black uppercase tracking-widest">
                <Phone weight="fill" size={12}/> {job.customer_phone}
              </a>
            )}
          </div>
        )}
        <button onClick={imHere} disabled={texting || job.en_route_at}
          data-testid="show-up-im-here"
          className={`h-14 px-6 border-2 border-[#0f172a] font-display font-black text-lg uppercase tracking-widest shadow-[3px_3px_0_#0f172a] transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed ${
            job.en_route_at ? "bg-emerald-500 text-white" : "bg-amber-400 hover:bg-amber-300 text-slate-900"
          }`}>
          <PaperPlaneTilt weight="fill" size={18} className="inline mr-1 -mt-1"/>
          {job.en_route_at ? "TENANT NOTIFIED ✓" : (texting ? "SENDING…" : "I'M HERE — TEXT TENANT")}
        </button>
        <WOVoiceNoteButton jobId={job.id} onSaved={onReload}/>
      </div>

      {/* What's broken (compact) */}
      {job.description && (
        <div className="p-3 border-t-2 border-slate-200 bg-slate-50">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
            <ClipboardText weight="bold" size={12}/> What's broken
          </div>
          <div className="text-sm text-slate-800 whitespace-pre-wrap">{job.description}</div>
          <TruckStockChip jobId={job.id}/>
          <PhotoOverlayGuide jobId={job.id} title={job.title} description={job.description}/>
        </div>
      )}
    </div>
  );
}

function CodeChip({ icon, label, value, onCopy }) {
  return (
    <button onClick={() => onCopy(label, value)}
      data-testid={`code-chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="text-left p-2 bg-white border-2 border-cyan-300 hover:border-cyan-600 hover:shadow-[2px_2px_0_#0891b2] transition-shadow">
      <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-cyan-800">
        {icon} {label}
      </div>
      <div className="font-mono font-black text-base leading-none mt-1 flex items-center gap-1">
        {value} <Copy size={10} className="text-slate-400"/>
      </div>
    </button>
  );
}
